/**
 * SplitEstimateModal - Split an approved estimate's line items into multiple jobs or invoices
 */
import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { apiHelpers } from '@/lib/api';
import { 
  Briefcase, Receipt, Plus, Trash2, Check, ArrowRight, Loader2, GripVertical
} from 'lucide-react';

const SplitEstimateModal = ({ open, onOpenChange, estimate, onComplete }) => {
  const { organization, user } = useAuth();
  const [mode, setMode] = useState('jobs'); // 'jobs' or 'invoices'
  const [groups, setGroups] = useState([{ line_item_indices: [], title: '', start_date: '', due_date: '' }]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get the "items" from the estimate based on display mode
  const items = useMemo(() => {
    if (!estimate) return [];
    const dm = estimate.display_mode || 'detailed';
    if (dm === 'simple') {
      return (estimate.simple_pricing || []).map((p, i) => ({
        index: i,
        name: p.title || `Option ${i + 1}`,
        description: '',
        total: p.price || 0,
      }));
    }
    if (dm === 'summary') {
      return (estimate.summary_sections || []).map((s, i) => ({
        index: i,
        name: s.title || `Section ${i + 1}`,
        description: s.description || '',
        total: estimate.summary_total ? (estimate.summary_total / (estimate.summary_sections?.length || 1)) : 0,
      }));
    }
    // detailed or sections
    return (estimate.line_items || []).map((item, i) => ({
      index: i,
      name: item.name || item.description || `Item ${i + 1}`,
      description: item.description || '',
      qty: item.qty,
      unit: item.unit,
      rate: item.rate,
      total: item.total || (item.qty * item.rate),
    }));
  }, [estimate]);

  // Which items are assigned to any group
  const assignedIndices = useMemo(() => {
    const set = new Set();
    groups.forEach(g => g.line_item_indices.forEach(i => set.add(i)));
    return set;
  }, [groups]);

  const unassignedItems = items.filter(item => !assignedIndices.has(item.index));

  const toggleItemInGroup = (groupIdx, itemIndex) => {
    setGroups(prev => prev.map((g, gi) => {
      if (gi !== groupIdx) return g;
      const has = g.line_item_indices.includes(itemIndex);
      return {
        ...g,
        line_item_indices: has
          ? g.line_item_indices.filter(i => i !== itemIndex)
          : [...g.line_item_indices, itemIndex]
      };
    }));
  };

  const addGroup = () => {
    setGroups(prev => [...prev, { line_item_indices: [], title: '', start_date: '', due_date: '' }]);
  };

  const removeGroup = (idx) => {
    if (groups.length <= 1) return;
    setGroups(prev => prev.filter((_, i) => i !== idx));
  };

  const updateGroup = (idx, field, value) => {
    setGroups(prev => prev.map((g, i) => i === idx ? { ...g, [field]: value } : g));
  };

  const getGroupSubtotal = (group) => {
    return group.line_item_indices.reduce((sum, i) => {
      const item = items.find(it => it.index === i);
      return sum + (item?.total || 0);
    }, 0);
  };

  const handleSubmit = async () => {
    const validGroups = groups.filter(g => g.line_item_indices.length > 0);
    if (validGroups.length === 0) {
      toast.error('Add at least one item to a group');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = validGroups.map(g => ({
        line_item_indices: g.line_item_indices,
        title: g.title || undefined,
        start_date: g.start_date || undefined,
        due_date: g.due_date || undefined,
      }));

      if (mode === 'jobs') {
        const res = await apiHelpers.splitEstimateToJobs(
          organization.id, estimate.id, payload, user.id, user.name
        );
        toast.success(res.data.message);
      } else {
        const res = await apiHelpers.splitEstimateToInvoices(
          organization.id, estimate.id, payload, user.id, user.name
        );
        toast.success(res.data.message);
      }
      onComplete?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to split estimate');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!estimate) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="split-estimate-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Split Estimate #{estimate.id?.slice(0, 8)}
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Assign line items to separate {mode} groups. Each group creates a new {mode === 'jobs' ? 'job' : 'invoice'}.
          </p>
        </DialogHeader>

        {/* Mode Toggle */}
        <div className="flex gap-2 mb-4" data-testid="split-mode-toggle">
          <Button
            variant={mode === 'jobs' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('jobs')}
            data-testid="split-mode-jobs"
          >
            <Briefcase className="w-4 h-4 mr-1" /> Split to Jobs
          </Button>
          <Button
            variant={mode === 'invoices' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('invoices')}
            data-testid="split-mode-invoices"
          >
            <Receipt className="w-4 h-4 mr-1" /> Split to Invoices
          </Button>
        </div>

        {/* Unassigned Items */}
        {unassignedItems.length > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="unassigned-items">
            <p className="text-xs font-medium text-amber-700 mb-2">
              Unassigned Items ({unassignedItems.length})
            </p>
            <div className="space-y-1">
              {unassignedItems.map(item => (
                <div key={item.index} className="flex items-center justify-between text-sm py-1 px-2 rounded bg-white border">
                  <span className="font-medium truncate flex-1">{item.name}</span>
                  <span className="text-muted-foreground ml-2">${item.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Groups */}
        <div className="space-y-4">
          {groups.map((group, gIdx) => (
            <div 
              key={gIdx} 
              className="border rounded-lg p-4 bg-card"
              data-testid={`split-group-${gIdx}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                  <h4 className="font-medium text-sm">
                    {mode === 'jobs' ? 'Job' : 'Invoice'} Group {gIdx + 1}
                  </h4>
                  <Badge variant="secondary" className="text-xs">
                    ${getGroupSubtotal(group).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Badge>
                </div>
                {groups.length > 1 && (
                  <Button 
                    variant="ghost" size="sm" 
                    onClick={() => removeGroup(gIdx)}
                    data-testid={`remove-group-${gIdx}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>

              {/* Group title and date */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <Label className="text-xs">Title (optional)</Label>
                  <Input
                    placeholder={`Phase ${gIdx + 1}`}
                    value={group.title}
                    onChange={e => updateGroup(gIdx, 'title', e.target.value)}
                    className="h-8 text-sm"
                    data-testid={`group-title-${gIdx}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">{mode === 'jobs' ? 'Start Date' : 'Due Date'}</Label>
                  <Input
                    type="date"
                    value={mode === 'jobs' ? group.start_date : group.due_date}
                    onChange={e => updateGroup(gIdx, mode === 'jobs' ? 'start_date' : 'due_date', e.target.value)}
                    className="h-8 text-sm"
                    data-testid={`group-date-${gIdx}`}
                  />
                </div>
              </div>

              {/* Item selection */}
              <div className="space-y-1">
                {items.map(item => {
                  const isInThisGroup = group.line_item_indices.includes(item.index);
                  const isInOtherGroup = !isInThisGroup && assignedIndices.has(item.index);
                  return (
                    <button
                      key={item.index}
                      onClick={() => !isInOtherGroup && toggleItemInGroup(gIdx, item.index)}
                      disabled={isInOtherGroup}
                      className={`w-full flex items-center gap-2 text-sm py-1.5 px-2 rounded transition-colors text-left ${
                        isInThisGroup
                          ? 'bg-blue-50 border border-blue-300 text-blue-900'
                          : isInOtherGroup
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-transparent'
                          : 'bg-gray-50 hover:bg-blue-50/50 border border-transparent hover:border-blue-200'
                      }`}
                      data-testid={`item-toggle-${gIdx}-${item.index}`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        isInThisGroup ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                      }`}>
                        {isInThisGroup && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="flex-1 truncate font-medium">{item.name}</span>
                      {item.qty && <span className="text-xs text-muted-foreground">{item.qty} {item.unit}</span>}
                      <span className="text-muted-foreground">${item.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <Button variant="outline" size="sm" onClick={addGroup} className="w-full mt-2" data-testid="add-split-group">
          <Plus className="w-4 h-4 mr-1" /> Add Another Group
        </Button>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="split-cancel-btn">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting}
            data-testid="split-submit-btn"
          >
            {isSubmitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
            ) : (
              <><ArrowRight className="w-4 h-4 mr-2" /> Create {groups.filter(g => g.line_item_indices.length > 0).length} {mode === 'jobs' ? 'Jobs' : 'Invoices'}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SplitEstimateModal;
